import type { IPascoRepository } from "@src/domain/pascos/repository/pasco_repository";
import type { PascoDTO } from "../dto";
import { PascoDtoMapper } from "../mappers/pasco-dto.mapper";
import type { CreatePascoData } from "@src/domain/pascos/value_objects/create_pasco_data";

/**
 * Use Case: Create PASCO
 * Creates a new PASCO record with file upload
 */
export class CreatePascoUseCase {
    constructor(private readonly pascoRepository: IPascoRepository) { }

    /**
     * Execute the use case
     * @param data - The PASCO creation data including file and metadata
     * @param onUploadProgress - Optional callback for upload progress
     * @returns Promise<PascoDTO> - The created PASCO DTO
     * @throws Error if validation fails
     */
    async execute(
        data: CreatePascoData,
        onUploadProgress?: (progress: number) => void
    ): Promise<PascoDTO> {
        this.validateCreateData(data);
        const pasco = await this.pascoRepository.create(data, onUploadProgress);
        return PascoDtoMapper.toDTO(pasco);
    }

    /**
     * Validate create data
     * @param data - The data to validate
     * @throws Error if validation fails
     */
    private validateCreateData(data: CreatePascoData): void {
        if (!data.file) {
            throw new Error('File is required');
        }

        if (!data.university_id || data.university_id.trim() === '') {
            throw new Error('University is required');
        }

        // Validate academic year format if provided (e.g., "2023/2024" or "2023-2024")
        if (data.academic_year && data.academic_year.trim() !== '') {
            const academicYearRegex = /^\d{4}(\/|-)\d{4}$/;
            if (!academicYearRegex.test(data.academic_year)) {
                throw new Error('Academic year must be in format YYYY/YYYY or YYYY-YYYY (e.g., 2023/2024)');
            }
        }
    }
}
